import api from "./axios";
import type {
    QuestionBank,
    CreateQuestionBankData,
    UpdateQuestionBankData,
} from "@/types/questionBank";

export type {
    QuestionBank,
    QuestionOption,
    QuestionBankFilters,
    CreateQuestionBankData,
    UpdateQuestionBankData,
    QuestionBankResponse,
} from "@/types/questionBank";

export const questionBankApi = {
    async getAll(params?: {
        page?: number;
        per_page?: number;
        search?: string;
        subject_id?: string;
        teacher_id?: string;
        type?: string;
        difficulty?: string;
        tags?: string;
        is_active?: boolean;
    }): Promise<{
        success: boolean;
        message: string;
        data: { items: QuestionBank[]; meta: any };
    }> {
        const response = await api.get("/v1/question-banks", { params });
        return response.data;
    },

    async getById(id: string): Promise<QuestionBank> {
        const response = await api.get(`/v1/question-banks/${id}`);
        return response.data.data;
    },

    async create(data: CreateQuestionBankData): Promise<QuestionBank> {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                if (value instanceof File) {
                    formData.append(key, value);
                } else if (Array.isArray(value)) {
                    formData.append(key, JSON.stringify(value));
                } else if (typeof value === "boolean") {
                    formData.append(key, value ? "1" : "0");
                } else {
                    formData.append(key, String(value));
                }
            }
        });
        const response = await api.post("/v1/question-banks", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data.data;
    },

    async update(
        id: string,
        data: UpdateQuestionBankData,
    ): Promise<QuestionBank> {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
            // Always include remove_image flag if it exists
            if (key === "remove_image" && value !== undefined) {
                formData.append(key, value ? "1" : "0");
            } else if (value !== undefined && value !== null) {
                if (value instanceof File) {
                    formData.append(key, value);
                } else if (Array.isArray(value)) {
                    formData.append(key, JSON.stringify(value));
                } else if (typeof value === "boolean") {
                    formData.append(key, value ? "1" : "0");
                } else {
                    formData.append(key, String(value));
                }
            }
        });
        const response = await api.post(
            `/v1/question-banks/${id}?_method=PUT`,
            formData,
            {
                headers: { "Content-Type": "multipart/form-data" },
            },
        );
        return response.data.data;
    },

    async delete(id: string): Promise<void> {
        await api.delete(`/v1/question-banks/${id}`);
    },

    async getBySubject(subjectId: string): Promise<QuestionBank[]> {
        const response = await api.get(
            `/v1/question-banks/by-subject/${subjectId}`,
        );
        return response.data.data || [];
    },

    async getByTeacher(teacherId: string): Promise<QuestionBank[]> {
        const response = await api.get(
            `/v1/question-banks/by-teacher/${teacherId}`,
        );
        return response.data.data || [];
    },

    async importQuestions(formData: FormData): Promise<{
        success: boolean;
        message: string;
        data?: any[];
    }> {
        const response = await api.post("/v1/question-banks/import", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    },

    async removeImage(id: string): Promise<QuestionBank> {
        const response = await api.post(
            `/v1/question-banks/${id}/remove-image`,
        );
        return response.data.data;
    },
};
